import { BigNumber } from 'bignumber.js';
import OrderType from '@/models/entities/cart/order-type';
import CartItemControls from '@/modules/cart/CartItemControls';
import ItemSummary from '@/models/entities/items/item-summary';
import {
  getEachCountFromQuantity,
  isAdditionalScanCode,
  isSoldByAverageWeight,
  isSoldByRandomWeight,
  isSoldByWeight,
  isWeightAndQuantityCustomizable,
} from '@/utils/item-utils';
import { LINE_ITEM_TYPE } from '@/constants/ApiConstants';
import { ENTRY_METHOD, EntryMethod } from '@/models/entities/cart/entry-method';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import ToastService from '@/modules/toasts/services/ToastService';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ModalService from '@/modules/modals/services/modal.service';
import WCStoreSelectModal from '@/components/WCStoreSelectModal/WCStoreSelectModal.vue';
import GiftCardLineItem from '@/models/entities/cart/gift-card-line-item';
import InvalidQuantityError from '@/models/entities/cart/invalid-quantity-error';
import PriceEmbeddedLineItem from '@/store/modules/cart/price-embedded-line-item';
import { Variation } from '@/store/modules/cart/variation';
import { Translate } from '@/types/translate';
import {
  CartAlertHandler,
  CartAlertOptions,
  CartControlsConfiguration,
  CartIsGiftCardVuexGetter,
  CustomerModeScanStateVuexGetter,
  GetCartsVuexAction,
  NonEmptyCartVuexGetter,
  RemoveEGiftCardVuexAction,
  SetCartsLoadingStatusVuexAction,
  SetItemQuantityVuexAction,
  StoreConfigurationGetter,
  UserVuexGetter,
} from '@/modules/cart/composition-configuration';

export default class CartControls {
  private readonly $t: Translate;

  private readonly getConfiguration: StoreConfigurationGetter;

  private readonly getNonEmptyCarts: NonEmptyCartVuexGetter;

  private readonly cartIsGiftCard: CartIsGiftCardVuexGetter;

  private readonly getUser: UserVuexGetter;

  private readonly isCustomerModeScan: CustomerModeScanStateVuexGetter;

  private readonly setItemQuantity: SetItemQuantityVuexAction;

  private readonly getCarts: GetCartsVuexAction;

  private readonly setCartsLoadingStatus: SetCartsLoadingStatusVuexAction;

  private readonly removeEGiftCard: RemoveEGiftCardVuexAction;

  private readonly getOrderType: () => OrderType;

  private readonly cartItemControls: CartItemControls;

  private readonly alertHandler: CartAlertHandler;

  constructor(configuration: CartControlsConfiguration) {
    this.$t = configuration.translate;
    this.getConfiguration = configuration.storeConfigurationGetter;
    this.getNonEmptyCarts = configuration.nonEmptyCartVuexGetter;
    this.cartIsGiftCard = configuration.cartIsGiftCardVuexGetter;
    this.getUser = configuration.userVuexGetter;
    this.isCustomerModeScan = configuration.customerModeScanStateVuexGetter;
    this.setItemQuantity = configuration.setItemQuantityVuexAction;
    this.getCarts = configuration.getCartsVuexAction;
    this.setCartsLoadingStatus = configuration.setCartsLoadingStatusVuexAction;
    this.removeEGiftCard = configuration.removeEGiftCardVuexAction;
    this.getOrderType = configuration.orderTypePropGetter;
    this.cartItemControls = new CartItemControls(configuration);
    this.alertHandler = configuration.alertHandler ?? CartControls.defaultAlertHandler;
  }

  private static readonly ALERTS: Record<
    'itemAddedToCart' | 'maximumQuantityReached' | 'failedToAddToCart',
    CartAlertOptions
  > = {
    itemAddedToCart: {
      variant: 'success',
      title: 'Cart',
      message: 'Item added to your Cart',
      timeout: 2000,
    },
    maximumQuantityReached: {
      variant: 'warning',
      title: 'Inventory',
      message: 'Maximum quantity reached',
      timeout: 7000,
    },
    failedToAddToCart: {
      variant: 'danger',
      title: 'Error',
      message: 'Failed to add the item to your cart',
      timeout: 10000,
    },
  };

  /**
   * Legacy alert behavior, used unless a configuration overrides `alertHandler`.
   */
  static defaultAlertHandler({ variant, title, message, timeout }: CartAlertOptions) {
    ToastService.open(WCSimpleToast, {
      props: { variant, title, message },
      actions: {},
      timeout,
      skipHash: false,
    });
  }

  /**
   * Get the rounded quantity set by the backend.
   *
   * This quantity can be slightly inaccurate, if the select increment has more decimals than the
   * weightProfile indicates, the extra precision will be rounded off.
   */
  getQuantityInCart(item: ItemSummary, variation: Variation) {
    const lineItem = this.cartItemControls.getItemLineItem(item, variation);
    return new BigNumber(lineItem ? lineItem.quantity : 0);
  }

  /**
   * Recover the unrounded quantity based on the rounded quantity in the cart.
   */
  recoverUnroundedQuantityFromCart(item: ItemSummary, variation: Variation) {
    const quantityInCart = this.getQuantityInCart(item, variation);
    const numIncrements = CartControls.getNumQuantityIncrements(item, quantityInCart);
    if (numIncrements.isZero()) {
      return new BigNumber(0);
    }
    const minimumQuantity = CartControls.getMinimumQuantity(item);
    const quantityIncrement = CartControls.getQuantityIncrement(item);
    return minimumQuantity.plus(numIncrements.minus(1).multipliedBy(quantityIncrement));
  }

  /**
   * Get the weighted quantity of the item in the cart, or 0 if unweighted.
   */
  getWeightedQuantityInCart(item: ItemSummary, variation: Variation) {
    const lineItem = this.cartItemControls.getItemLineItem(item, variation);
    return new BigNumber(lineItem ? lineItem.weightedItemQuantity : 0);
  }

  /**
   * Get the total quantity of the base item in the cart.
   * @see getTotalBaseQuantity
   */
  getTotalBaseQuantityInCart(item: ItemSummary, variation: Variation) {
    const quantityInCart = this.recoverUnroundedQuantityFromCart(item, variation);
    const weightedQuantityInCart = this.getWeightedQuantityInCart(item, variation);
    return CartControls.getTotalBaseQuantity(item, quantityInCart, weightedQuantityInCart);
  }

  /**
   * Get the total quantity of the base item, accounting for weightedQuantity and ASC quantity.
   *
   * It is not possible for an item to be an ASC item and a weighted item. ASC items are already
   * "packs" of a defined quantity (weight). Having 2 packs or 1 pack with weighted quantity of
   * 2 is the same, and it would be confusing to have both options.
   */
  static getTotalBaseQuantity(
    item: ItemSummary,
    quantity: BigNumber,
    weightedQuantity: BigNumber | null,
  ) {
    let totalBaseQuantity = quantity;
    if (isSoldByRandomWeight(item)) {
      if (!weightedQuantity) {
        throw new Error('Cannot compute total base quantity without weighted quantity.');
      }
      totalBaseQuantity = totalBaseQuantity.multipliedBy(weightedQuantity);
    }
    if (isAdditionalScanCode(item)) {
      totalBaseQuantity = totalBaseQuantity.multipliedBy(item.ascQuantity);
    }
    return totalBaseQuantity;
  }

  /**
   * Get the quantity increment value, based on the type of item.
   */
  static getQuantityIncrement(item: ItemSummary) {
    if (isSoldByAverageWeight(item)) {
      return new BigNumber(item.averageWeight);
    }
    if (isSoldByRandomWeight(item)) {
      return new BigNumber(item.weightProfile.selectIncrement);
    }
    return new BigNumber(1);
  }

  /**
   * Get the weighted quantity increment value, based on the type of item.
   *
   * Only random weighted items can be incremented.
   */
  static getWeightedQuantityIncrement(item: ItemSummary) {
    if (!isSoldByRandomWeight(item)) {
      throw new Error(
        'Weighted quantity increment is not useful for items not sold by random weight',
      );
    }
    return new BigNumber(1);
  }

  /**
   * Get the quantity of an item in the cart to display in the UI.
   * @see getDisplayQuantity
   */
  getDisplayQuantityInCart(item: ItemSummary, variation: Variation) {
    return CartControls.getDisplayQuantity(
      item,
      this.recoverUnroundedQuantityFromCart(item, variation),
    );
  }

  /**
   * Return true if the item is sold by random weight and
   * has the allow weighted quantity enabled in the weight profile
   */
  static isSoldByRandomWeightWithDualAdjusters(item: ItemSummary) {
    return isSoldByRandomWeight(item) && isWeightAndQuantityCustomizable(item);
  }

  /**
   * Get the quantity of an item to display in the UI, based on the type of item.
   */
  static getDisplayQuantity(item: ItemSummary, quantity: BigNumber) {
    if (isSoldByAverageWeight(item)) {
      return getEachCountFromQuantity(item, quantity).toString();
    }
    if (isSoldByRandomWeight(item)) {
      return CartControls.getDisplayRandomWeightFromQuantity(item, quantity).toString();
    }
    return quantity.toString();
  }

  /**
   * Get the display quantity for an item sold by random weight, rounding to the profile's decimal
   * places and including the units.
   */
  static getDisplayRandomWeightFromQuantity(item: ItemSummary, quantity: BigNumber) {
    if (!isSoldByRandomWeight(item)) {
      throw new Error(
        'Display Weighted Quantity is not defined for items not sold by random weight',
      );
    }
    const decimals = item.weightProfile.decimals;
    const roundedQuantity = quantity.toFixed(decimals, BigNumber.ROUND_HALF_UP);
    const abbrv = item.weightProfile.abbrv;
    return `${roundedQuantity} ${abbrv}`;
  }

  /**
   * Get the weighted quantity of an item in the cart to display in the UI.
   * @see getDisplayWeightedQuantity
   */
  getDisplayWeightedQuantityInCart(item: ItemSummary, variation: Variation) {
    return CartControls.getDisplayWeightedQuantity(
      item,
      this.getWeightedQuantityInCart(item, variation),
    );
  }

  /**
   * Get the weighted quantity of an item to display in the UI.
   *
   * Only random weighted items have a weighted quantity.
   */
  static getDisplayWeightedQuantity(item: ItemSummary, quantity: BigNumber) {
    if (!isSoldByRandomWeight(item)) {
      throw new Error('Weighted Quantity does not exist for items not sold by random weight');
    }
    return quantity.toString();
  }

  /**
   * The minimum quantity of an item that can be added to the cart.
   */
  static getMinimumQuantity(item: ItemSummary) {
    if (isSoldByAverageWeight(item)) {
      return new BigNumber(item.averageWeight);
    }
    if (isSoldByRandomWeight(item)) {
      return new BigNumber(item.weightProfile.selectMinimum);
    }
    return new BigNumber(1);
  }

  /**
   * The maximum total base quantity of an item that can be added to the cart.
   */
  getMaximumTotalBaseQuantity(item: ItemSummary) {
    if (this.isCustomerModeScan()) {
      return new BigNumber('Infinity');
    }

    const departmentMax = CartControls.getDepartmentMax(item);
    const selectMax = CartControls.getSelectMax(item);
    const onHand = CartControls.getOnHand(item);

    if (item.sellOutOfStock) {
      return BigNumber.minimum(departmentMax, selectMax);
    }
    return BigNumber.minimum(departmentMax, selectMax, onHand);
  }

  /**
   * Get the department-set-maximum quantity of an item that can be added to the cart.
   */
  static getDepartmentMax(item: ItemSummary) {
    return new BigNumber(item.maxQuantity);
  }

  /**
   * Get the maximum quantity of an item that can be added to the cart based on the weight profile.
   * (Infinity if unweighted or sold by average weight)
   */
  static getSelectMax(item: ItemSummary) {
    if (!isSoldByWeight(item) || isSoldByAverageWeight(item)) {
      return new BigNumber('Infinity');
    }
    const selectIncrement = new BigNumber(item.weightProfile.selectIncrement);
    const selectMinimum = new BigNumber(item.weightProfile.selectMinimum);
    const selectCount = new BigNumber(item.weightProfile.selectCount);
    return selectMinimum.plus(selectIncrement.multipliedBy(selectCount.minus(1)));
  }

  /**
   * Get the on-hand amount of an item, accounting for ASC quantity.
   */
  static getOnHand(item: ItemSummary) {
    return new BigNumber(item.maxBaseQuantity);
  }

  /**
   * Get the minimum weighted quantity of an item that can be added to the cart.
   *
   * Only items sold by weight have a useful weighted quantity.
   */
  static getMinimumWeightedQuantity(item: ItemSummary) {
    if (isSoldByWeight(item)) {
      return new BigNumber(1);
    }
    return new BigNumber('NaN');
  }

  /**
   * Check if a quantity of an item satisfies the RACS quantity limits.
   *
   * Considers the entire cart, if `item`'s total quantity were to be `totalBaseQuantity`.
   * @see {@link https://docs.ecrs.com/regulatory-compliance-and-certifications/juul-labs-racs-retail-access-control-standards}
   */
  isQuantityValidByRacs(item: ItemSummary, variation: Variation, totalBaseQuantity: BigNumber) {
    const quantityLimit = item.quantityLimitData?.quantityLimit;
    if (!quantityLimit) {
      return true;
    }
    const currentTotalQuantityForVendorGroup = this.getTotalQuantityForVendorGroup(item);
    const currentRacsPackQuantityInCart = this.getRacsPackQuantityInCart(item, variation);
    const candidateRacsPackQuantity = CartControls.getRacsPackQuantity(item, totalBaseQuantity);
    const candidateTotalQuantityForVendorGroup = currentTotalQuantityForVendorGroup
      .minus(currentRacsPackQuantityInCart)
      .plus(candidateRacsPackQuantity);
    return candidateTotalQuantityForVendorGroup.isLessThanOrEqualTo(quantityLimit);
  }

  /**
   * The total quantity of all items in the cart of a given vendor group,
   * accounting for each item's pack quantity.
   */
  getTotalQuantityForVendorGroup(item: ItemSummary) {
    const lineItems = this.cartItemControls.getLineItemsOfType(LINE_ITEM_TYPE.INVENTORY_ITEM);
    const matchVendorGroup = item.quantityLimitData.vendorGroup;
    const lineItemsInVendorGroup = lineItems.filter(
      li => li.item.quantityLimitData?.vendorGroup === matchVendorGroup,
    );
    return lineItemsInVendorGroup.reduce(
      (total, li) => total.plus(this.getRacsPackQuantityInCart(li.item, li.variation)),
      new BigNumber(0),
    );
  }

  /**
   * Get the pack quantity of an item in the cart.
   */
  getRacsPackQuantityInCart(item: ItemSummary, variation: Variation) {
    return CartControls.getRacsPackQuantity(item, this.getTotalBaseQuantityInCart(item, variation));
  }

  /**
   * Get the pack quantity of an item, given its quantity.
   */
  static getRacsPackQuantity(item: ItemSummary, totalBaseQuantity: BigNumber) {
    if (!item.quantityLimitData) {
      return new BigNumber(0);
    }
    return totalBaseQuantity.multipliedBy(item.quantityLimitData.comboPackAmount);
  }

  /**
   * Get the number of quantity increments that have occurred based on a given quantity.
   *
   * The number of increments is the number of times the user would need to consecutively
   * press the add button to achieve the current quantity (beginning with quantity = 0).
   *
   * Equation for quantity given numIncrements:
   * ```
   * quantity = 0                                                       (numIncrements == 0)
   * quantity = minQuantity + ((numIncrements - 1) * incrementValue)    (numIncrements >= 1)
   * ---
   * Solved for numIncrements, we have:
   * numIncrements = 0                                                  (quantity == 0)
   * numIncrements = ((quantity - minQuantity) / incrementValue) + 1    (quantity > 0)
   * ```
   */
  static getNumQuantityIncrements(item: ItemSummary, quantity: BigNumber) {
    if (quantity.isZero()) {
      return new BigNumber(0);
    }
    const minimumQuantity = CartControls.getMinimumQuantity(item);
    const quantityIncrement = CartControls.getQuantityIncrement(item);
    if (quantityIncrement.isLessThanOrEqualTo(0)) {
      return new BigNumber(0);
    }
    return quantity
      .minus(minimumQuantity)
      .dividedBy(quantityIncrement)
      .integerValue(BigNumber.ROUND_HALF_UP)
      .plus(1);
  }

  /**
   * Get an item's next quantity after an increment.
   */
  static getNextQuantityIncrement(item: ItemSummary, currentQuantity: BigNumber) {
    if (currentQuantity.isZero()) {
      return CartControls.getMinimumQuantity(item);
    }
    const quantityIncrement = CartControls.getQuantityIncrement(item);
    return currentQuantity.plus(quantityIncrement);
  }

  /**
   * Get a cart item's next quantity after an increment.
   */
  getNextQuantityIncrementInCart(item: ItemSummary, variation: Variation) {
    const unroundedQuantity = this.recoverUnroundedQuantityFromCart(item, variation);
    return CartControls.getNextQuantityIncrement(item, unroundedQuantity);
  }

  /**
   * Get an item's next quantity after a decrement.
   */
  static getNextQuantityDecrement(item: ItemSummary, currentQuantity: BigNumber) {
    if (currentQuantity.isLessThanOrEqualTo(CartControls.getMinimumQuantity(item))) {
      return new BigNumber(0);
    }
    const quantityIncrement = CartControls.getQuantityIncrement(item);
    return currentQuantity.minus(quantityIncrement);
  }

  /**
   * Get a cart item's next quantity after a decrement.
   */
  getNextQuantityDecrementInCart(item: ItemSummary, variation: Variation) {
    const unroundedQuantity = this.recoverUnroundedQuantityFromCart(item, variation);
    return CartControls.getNextQuantityDecrement(item, unroundedQuantity);
  }

  /**
   * Check if the next quantity increment of an item is allowed.
   * @param item The item.
   * @param currentQuantity `item`'s current quantity.
   * @param currentWeightedQuantity `item`'s current weighted quantity, if applicable.
   */
  isNextQuantityIncrementValid(
    item: ItemSummary,
    currentQuantity: BigNumber,
    currentWeightedQuantity: BigNumber,
  ) {
    const nextQuantity = CartControls.getNextQuantityIncrement(item, currentQuantity);
    return this.isQuantityValidByItemLimits(item, nextQuantity, currentWeightedQuantity);
  }

  /**
   * Check if the next quantity increment of an item in the cart is allowed.
   * @see isQuantityValidForCart
   */
  isNextQuantityIncrementInCartValid(item: ItemSummary, variation: Variation) {
    const {
      nextQuantity,
      nextWeightedQuantity,
    } = this.getNextQuantityIncrementWithWeightedQuantityInCart(item, variation);
    return this.isQuantityValidForCart(item, variation, nextQuantity, nextWeightedQuantity);
  }

  /**
   * Check if the next quantity decrement of an item is allowed.
   * @param item The item.
   * @param currentQuantity `item`'s current quantity.
   * @param currentWeightedQuantity `item`'s current weighted quantity, if applicable.
   */
  isNextQuantityDecrementValid(
    item: ItemSummary,
    currentQuantity: BigNumber,
    currentWeightedQuantity: BigNumber,
  ) {
    const nextQuantity = CartControls.getNextQuantityDecrement(item, currentQuantity);
    return this.isQuantityValidByItemLimits(item, nextQuantity, currentWeightedQuantity);
  }

  /**
   * Check if the next quantity decrement of an item in the cart is allowed.
   * @see isQuantityValidForCart
   */
  isNextQuantityDecrementInCartValid(item: ItemSummary, variation: Variation) {
    const nextQuantity = this.getNextQuantityDecrementInCart(item, variation);
    const weightedQuantity = this.getWeightedQuantityInCart(item, variation);
    return this.isQuantityValidForCart(item, variation, nextQuantity, weightedQuantity);
  }

  /**
   * Get an item's next weighted quantity after an increment.
   */
  static getNextWeightedQuantityIncrement(item: ItemSummary, currentWeightedQuantity: BigNumber) {
    if (currentWeightedQuantity.isZero()) {
      return CartControls.getMinimumWeightedQuantity(item);
    }
    const weightedQuantityIncrement = CartControls.getWeightedQuantityIncrement(item);
    return currentWeightedQuantity.plus(weightedQuantityIncrement);
  }

  /**
   * Get a cart item's next weighted quantity after an increment.
   */
  getNextWeightedQuantityIncrementInCart(item: ItemSummary, variation: Variation) {
    const weightedQuantity = this.getWeightedQuantityInCart(item, variation);
    return CartControls.getNextWeightedQuantityIncrement(item, weightedQuantity);
  }

  /**
   * Get an item's next weighted quantity after a decrement.
   */
  static getNextWeightedQuantityDecrement(item: ItemSummary, currentWeightedQuantity: BigNumber) {
    if (
      currentWeightedQuantity.isLessThanOrEqualTo(CartControls.getMinimumWeightedQuantity(item))
    ) {
      return new BigNumber(0);
    }
    const weightedQuantityIncrement = CartControls.getWeightedQuantityIncrement(item);
    return currentWeightedQuantity.minus(weightedQuantityIncrement);
  }

  /**
   * Get a cart item's next weighted quantity after a decrement.
   */
  getNextWeightedQuantityDecrementInCart(item: ItemSummary, variation: Variation) {
    const weightedQuantity = this.getWeightedQuantityInCart(item, variation);
    return CartControls.getNextWeightedQuantityDecrement(item, weightedQuantity);
  }

  /**
   * Check if the next weighted quantity increment of an item is allowed.
   */
  isNextWeightedQuantityIncrementValid(
    item: ItemSummary,
    currentQuantity: BigNumber,
    currentWeightedQuantity: BigNumber,
  ) {
    const nextWeightedQuantity = CartControls.getNextWeightedQuantityIncrement(
      item,
      currentWeightedQuantity,
    );
    return this.isQuantityValidByItemLimits(item, currentQuantity, nextWeightedQuantity);
  }

  /**
   * Check if the next weighted quantity increment of an item in the cart is allowed.
   * @see isQuantityValidForCart
   */
  isNextWeightedQuantityIncrementInCartValid(item: ItemSummary, variation: Variation) {
    const quantity = this.recoverUnroundedQuantityFromCart(item, variation);
    const nextWeightedQuantity = this.getNextWeightedQuantityIncrementInCart(item, variation);
    return this.isQuantityValidForCart(item, variation, quantity, nextWeightedQuantity);
  }

  /**
   * Check if the next weighted quantity decrement of an item is allowed.
   */
  isNextWeightedQuantityDecrementValid(
    item: ItemSummary,
    currentQuantity: BigNumber,
    currentWeightedQuantity: BigNumber,
  ) {
    const nextWeightedQuantity = CartControls.getNextWeightedQuantityDecrement(
      item,
      currentWeightedQuantity,
    );
    return this.isQuantityValidByItemLimits(item, currentQuantity, nextWeightedQuantity);
  }

  /**
   * Check if the next weighted quantity decrement of an item in the cart is allowed.
   * @see isQuantityValidForCart
   */
  isNextWeightedQuantityDecrementInCartValid(item: ItemSummary, variation: Variation) {
    const quantity = this.recoverUnroundedQuantityFromCart(item, variation);
    const nextWeightedQuantity = this.getNextWeightedQuantityDecrementInCart(item, variation);
    return this.isQuantityValidForCart(item, variation, quantity, nextWeightedQuantity);
  }

  getNextQuantityIncrementWithWeightedQuantityInCart(item: ItemSummary, variation: Variation) {
    const currentQuantity = this.recoverUnroundedQuantityFromCart(item, variation);
    const nextQuantity = this.getNextQuantityIncrementInCart(item, variation);
    let nextWeightedQuantity: BigNumber | null = null;
    if (isSoldByRandomWeight(item)) {
      if (currentQuantity.isZero()) {
        nextWeightedQuantity = this.getNextWeightedQuantityIncrementInCart(item, variation);
      } else {
        nextWeightedQuantity = this.getWeightedQuantityInCart(item, variation);
      }
    } else if (isSoldByAverageWeight(item)) {
      nextWeightedQuantity = CartControls.getMinimumWeightedQuantity(item);
    }
    return { currentQuantity, nextQuantity, nextWeightedQuantity };
  }

  /**
   * Check if a total base quantity for an item is allowed by its limits.
   *
   * - Cannot be less than zero.
   * - Cannot be in `(0, minimumQuantity]`.
   * - Cannot be greater than `maximumQuantity`.
   * - Quantity must be a valid increment.
   * - Weighted Quantity must be a valid increment.
   */
  isQuantityValidByItemLimits(
    item: ItemSummary,
    quantity: BigNumber,
    weightedQuantity: BigNumber | null,
  ) {
    const totalBaseQuantity = CartControls.getTotalBaseQuantity(item, quantity, weightedQuantity);
    if (totalBaseQuantity.isLessThan(0)) {
      return false;
    }

    // ascQuantity < 1 previously blocked adding a valid
    // pack: totalBaseQuantity (quantity * ascQuantity) fell below minimumQuantity even
    // though the requested pack quantity itself was valid. Using quantity here instead of
    // totalBaseQuantity fixes that, since quantity is already validated independently below.
    const hasInvalidAscQuantity =
      isAdditionalScanCode(item) && !new BigNumber(item.ascQuantity).isGreaterThanOrEqualTo(1);
    const referenceQuantity = hasInvalidAscQuantity ? quantity : totalBaseQuantity;
    if (referenceQuantity.isZero()) {
      return true;
    }
    if (hasInvalidAscQuantity && totalBaseQuantity.isNaN()) {
      return false;
    }

    const minimumQuantity = CartControls.getMinimumQuantity(item);
    if (referenceQuantity.isLessThan(minimumQuantity)) {
      return false;
    }
    const maximumTotalBaseQuantity = this.getMaximumTotalBaseQuantity(item);
    if (totalBaseQuantity.isGreaterThan(maximumTotalBaseQuantity)) {
      return false;
    }

    if (!CartControls.isQuantityValidIncrement(item, quantity)) {
      return false;
    }
    if (
      weightedQuantity &&
      !CartControls.isWeightedQuantityValidIncrement(item, weightedQuantity)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Check if a quantity is valid based on the minimum and increment.
   *
   * If the increment is <= 0, no increments are valid.
   */
  static isQuantityValidIncrement(item: ItemSummary, quantity: BigNumber) {
    const minimum = CartControls.getMinimumQuantity(item);
    const increment = CartControls.getQuantityIncrement(item);
    if (increment.isLessThanOrEqualTo(0)) {
      return false;
    }
    return quantity
      .minus(minimum)
      .dividedBy(increment)
      .isInteger();
  }

  /**
   * Check if a weighted quantity is valid based on the minimum and increment.
   *
   * If the item is sold by average weight, only the minimum is allowed,
   * it cannot be incremented or decremented.
   * If the item is not sold by weight, anything is valid.
   */
  static isWeightedQuantityValidIncrement(item: ItemSummary, weightedQuantity: BigNumber) {
    if (isSoldByAverageWeight(item)) {
      return weightedQuantity.isEqualTo(CartControls.getMinimumWeightedQuantity(item));
    }
    if (!isSoldByRandomWeight(item)) {
      return true;
    }

    const minimum = CartControls.getMinimumWeightedQuantity(item);
    const increment = CartControls.getWeightedQuantityIncrement(item);
    return weightedQuantity
      .minus(minimum)
      .dividedBy(increment)
      .isInteger();
  }

  /**
   * Check if a total base quantity for an item is allowed in the cart.
   *
   * - Must be valid by the item limits.
   * - Must be valid by the RACS quantity limits.
   * @see isQuantityValidByItemLimits
   * @see isQuantityValidByRacs
   * @param item The item.
   * @param variation `item`'s variation in the cart.
   * @param quantity `item`'s quantity to check.
   * @param weightedQuantity `item`'s weighted quantity to check, if applicable.
   */
  isQuantityValidForCart(
    item: ItemSummary,
    variation: Variation,
    quantity: BigNumber,
    weightedQuantity: BigNumber | null,
  ) {
    const totalBaseQuantity = CartControls.getTotalBaseQuantity(item, quantity, weightedQuantity);
    const validByItemLimits = this.isQuantityValidByItemLimits(item, quantity, weightedQuantity);
    const validByRacs = this.isQuantityValidByRacs(item, variation, totalBaseQuantity);
    return validByItemLimits && validByRacs;
  }

  /**
   * Check if an item is in stock.
   */
  static isInStock(item: ItemSummary) {
    // If the item is a service item i.e. maxBaseQuantity is not present,
    // mark the quantity as in stock
    if (item.sellOutOfStock || item.maxBaseQuantity === undefined) {
      return true;
    }
    return CartControls.getOnHand(item).isGreaterThanOrEqualTo(
      CartControls.getMinimumQuantity(item),
    );
  }

  /**
   * Check if an item should be presented as out of stock. Scan mode shoppers are holding
   * the item, so stock levels never block them.
   */
  isOutOfStockForDisplay(item: ItemSummary) {
    return !CartControls.isInStock(item) && !this.isCustomerModeScan();
  }

  /**
   * Check if an item is sold in stores only.
   */
  static isInStoreOnly(item: ItemSummary) {
    return item.maxQuantity <= 0 || item.actualPrice < 0;
  }

  /**
   * Check if an item should be presented as in-store only. Scan mode shoppers are already in
   * the store, so this never applies to them.
   */
  isInStoreOnlyForDisplay(item: ItemSummary) {
    return CartControls.isInStoreOnly(item) && !this.isCustomerModeScan();
  }

  /**
   *
   * Check if an Item is available
   */
  static isAvailable(item: ItemSummary) {
    return !(
      (item.discontinued && !CartControls.isInStock(item)) ||
      (item.discontinued && CartControls.isInStoreOnly(item)) ||
      this.getMinimumQuantity(item).comparedTo(0) <= 0
    );
  }

  /**
   * Set a new quantity for an item in the cart as long as it is valid.
   */
  async setQuantity(
    quantity: number | string | BigNumber,
    weightedQuantity: number | string | BigNumber | null,
    item: ItemSummary,
    variation: Variation,
    priceEmbeddedLineItem?: PriceEmbeddedLineItem | null,
    showStoreSelectModal: boolean = true,
    entryMethod: EntryMethod = ENTRY_METHOD.DEFAULT,
  ) {
    let newQuantity = new BigNumber(quantity);

    let newWeightedQuantity = new BigNumber(weightedQuantity);
    if (newQuantity.isZero() || newWeightedQuantity.isZero()) {
      newQuantity = new BigNumber(0);
      newWeightedQuantity = new BigNumber(0);
    }
    const newTotalBaseQuantity = CartControls.getTotalBaseQuantity(
      item,
      newQuantity,
      newWeightedQuantity,
    );

    if (!this.isQuantityValidForCart(item, variation, newQuantity, newWeightedQuantity)) {
      throw CartControls.createQuantityError(
        item,
        newQuantity,
        newWeightedQuantity,
        newTotalBaseQuantity,
      );
    }

    if (this.cartIsGiftCard()) {
      this.alertGiftCardItem();
      throw new Error('Cannot add Gift Card Item to cart');
    }

    if (this.isFirstItemInCartAtNonHomeStore(newQuantity.toNumber()) && showStoreSelectModal) {
      await CartControls.alertNotShoppingFromHomeStore();
    }
    this.trackAnalyticsNewQuantity(newQuantity.toNumber(), item);

    const itemLineItem = this.cartItemControls.getItemLineItem(item, variation);

    await this.setItemQuantity({
      itemLineItem,
      item,
      variation,
      weightedItemQuantity: isSoldByWeight(item) ? newWeightedQuantity.toNumber() : null,
      quantity: newQuantity.toNumber(),
      priceEmbeddedLineItem,
      orderType: this.getOrderType(),
      entryMethod,
    });
  }

  /**
   * Increment the quantity of an item in the cart if possible.
   */
  async increment(
    item: ItemSummary,
    variation: Variation,
    priceEmbeddedLineItem: PriceEmbeddedLineItem | null,
    showStoreSelectModal: boolean,
    entryMethod: EntryMethod = ENTRY_METHOD.DEFAULT,
    showSuccessMessage = true,
  ) {
    const {
      currentQuantity,
      nextQuantity,
      nextWeightedQuantity,
    } = this.getNextQuantityIncrementWithWeightedQuantityInCart(item, variation);

    if (!this.isQuantityValidForCart(item, variation, nextQuantity, nextWeightedQuantity)) {
      this.alertMaximumQuantityReached();
      return;
    }

    try {
      await this.setQuantity(
        nextQuantity,
        nextWeightedQuantity,
        item,
        variation,
        priceEmbeddedLineItem,
        showStoreSelectModal,
        entryMethod,
      );
    } catch (error) {
      console.error(error);
      this.alertFailedToAddToCart();
      return;
    }

    if (showSuccessMessage && currentQuantity.isZero() && !this.isCustomerModeScan()) {
      this.alertItemAddedToCart();
    }
    if (!this.isNextQuantityIncrementInCartValid(item, variation)) {
      this.alertMaximumQuantityReached();
    }
  }

  /**
   * Decrement the quantity of an item in the cart if possible.
   */
  async decrement(
    item: ItemSummary,
    variation: Variation,
    priceEmbeddedLineItem: PriceEmbeddedLineItem | null,
  ) {
    const nextQuantity = this.getNextQuantityDecrementInCart(item, variation);
    const weightedQuantity = this.getWeightedQuantityInCart(item, variation);
    await this.setQuantity(nextQuantity, weightedQuantity, item, variation, priceEmbeddedLineItem);
  }

  /**
   * Increment the weighted quantity of an item in the cart if possible.
   *
   * Weighted Quantity adjusters do not accept price embedded data because
   * they do not exist in ACO, which is the only time price embedded items
   * can show up in WebCart.
   */
  async incrementWeightedQuantity(item: ItemSummary, variation: Variation) {
    const quantity = this.recoverUnroundedQuantityFromCart(item, variation);
    const nextWeightedQuantity = this.getNextWeightedQuantityIncrementInCart(item, variation);
    if (!this.isQuantityValidForCart(item, variation, quantity, nextWeightedQuantity)) {
      this.alertMaximumQuantityReached();
      return;
    }

    try {
      await this.setQuantity(quantity, nextWeightedQuantity, item, variation, null);
    } catch (error) {
      console.error(error);
      this.alertFailedToAddToCart();
    }
  }

  /**
   * Decrement the weighted quantity of an item in the cart if possible.
   *
   * Weighted Quantity adjusters do not accept price embedded data because
   * they do not exist in ACO, which is the only time price embedded items
   * can show up in WebCart.
   */
  async decrementWeightedQuantity(item: ItemSummary, variation: Variation) {
    const quantity = this.recoverUnroundedQuantityFromCart(item, variation);
    const nextWeightedQuantity = this.getNextWeightedQuantityDecrementInCart(item, variation);
    await this.setQuantity(quantity, nextWeightedQuantity, item, variation, null);
  }

  /**
   * Increment an item with a single unified adjuster.
   *
   * For items sold by random weight, this increments the weighted quantity, unless the weighted
   * quantity adjuster is hidden.
   * For all others, this increments the quantity.
   */
  async incrementCombinedQuantities(item: ItemSummary, variation: Variation) {
    if (CartControls.isSoldByRandomWeightWithDualAdjusters(item)) {
      await this.incrementWeightedQuantity(item, variation);
    } else {
      await this.increment(item, variation, null, false);
    }
  }

  /**
   * Decrement an item with a single unified adjuster.
   *
   * For items sold by random weight, this decrements the weighted quantity, unless the weighted
   * quantity adjuster is hidden.
   * For all others, this decrements the quantity.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   */
  async decrementCombinedQuantities(item: ItemSummary, variation: Variation) {
    if (CartControls.isSoldByRandomWeightWithDualAdjusters(item)) {
      await this.decrementWeightedQuantity(item, variation);
    } else {
      await this.decrement(item, variation, null);
    }
  }

  /**
   * Get the quantity of an item in the cart to display in the UI for combined adjusters.
   *
   * For items sold by random weight, this is the weighted quantity, unless the weighted
   * quantity adjuster is hidden.
   * For all others, this is the quantity.
   */
  getDisplayForCombinedQuantitiesInCart(item: ItemSummary, variation: Variation) {
    if (CartControls.isSoldByRandomWeightWithDualAdjusters(item)) {
      return this.getDisplayWeightedQuantityInCart(item, variation);
    }
    return this.getDisplayQuantityInCart(item, variation);
  }

  /**
   * Remove the item from the cart unconditionally.
   */
  async remove(item: ItemSummary, variation: Variation) {
    AnalyticsService.track(TrackableEventConstants.ItemRemovedFromCart, {
      items: [item],
      customerId: this.getUser()?.sub,
    });

    if (
      !this.getConfiguration().orderTypesEnabled ||
      (this.getConfiguration().orderTypesEnabled && this.isCustomerModeScan())
    ) {
      const itemLineItem = this.cartItemControls.getItemLineItem(item, variation);
      await this.setItemQuantity({
        itemLineItem,
        item,
        variation,
        quantity: 0,
        weightedItemQuantity: null,
        orderType: this.getOrderType(),
      });
    }

    if (
      (this.getConfiguration().orderTypesEnabled && this.cartItemControls.getLineItemCount() > 0) ||
      (this.getConfiguration().orderTypesEnabled && this.getNonEmptyCarts()?.length <= 1)
    ) {
      this.setCartsLoadingStatus(true);
      await this.setItemQuantity({
        item,
        variation,
        quantity: 0,
      });
      await this.getCarts();
      this.setCartsLoadingStatus(false);
    }
  }

  /**
   * Remove a gift card from the cart.
   */
  async removeGiftCard(giftCardItem: GiftCardLineItem) {
    try {
      await this.removeEGiftCard(giftCardItem);
    } catch (error) {
      console.log(error);
    } finally {
      if (this.getConfiguration().orderTypesEnabled) {
        await this.getCarts();
      }
    }
  }

  /**
   * Check if a new quantity is the first item added to an empty cart
   * and the logged-in user is not shopping at their home store.
   */
  isFirstItemInCartAtNonHomeStore(newQuantity: number) {
    return (
      this.cartItemControls.isEmpty() &&
      newQuantity > 0 &&
      this.getUser()?.home !== this.getConfiguration().store.id &&
      this.getConfiguration().multistore &&
      !this.isCustomerModeScan()
    );
  }

  /**
   * Track the new quantity added to cart with the analytics service.
   */
  trackAnalyticsNewQuantity(newQuantity: number, item: ItemSummary) {
    AnalyticsService.track(
      newQuantity > 0
        ? TrackableEventConstants.ItemAddedToCart
        : TrackableEventConstants.ItemRemovedFromCart,
      { items: [item], customerId: this.getUser()?.sub },
    );
  }

  /**
   * Create a human-readable Error describing that a quantity is invalid for an item.
   */
  static createQuantityError(
    item: ItemSummary,
    quantity: BigNumber,
    weightedQuantity: BigNumber,
    totalBaseQuantity: BigNumber,
  ) {
    const quantityDisplay = `Quantity: ${quantity.toString()} (${CartControls.getDisplayQuantity(
      item,
      quantity,
    )}),`;

    let weightedQuantityDisplay = '';
    if (isSoldByRandomWeight(item)) {
      const wqString = weightedQuantity.toString();
      const wqFormatted = CartControls.getDisplayWeightedQuantity(item, weightedQuantity);
      weightedQuantityDisplay = ` Weighted Quantity: ${wqString} (${wqFormatted}),`;
    }

    const totalBaseQuantityDisplay = `Total Base Quantity: ${totalBaseQuantity.toString()},`;

    const invalidText = `is invalid for item "${item.name}" (${item.id})`;

    return new InvalidQuantityError(
      `${quantityDisplay}${weightedQuantityDisplay} ${totalBaseQuantityDisplay} ${invalidText}`,
    );
  }

  /**
   * Alert the user that an item was added to their cart, always via the legacy WCSimpleToast.
   * @see alertItemAddedToCart the instance method, which respects a configured `alertHandler`.
   */
  static alertItemAddedToCart() {
    CartControls.defaultAlertHandler(CartControls.ALERTS.itemAddedToCart);
  }

  /**
   * Alert the user that an item was added to their cart.
   */
  alertItemAddedToCart() {
    this.alertHandler(CartControls.ALERTS.itemAddedToCart);
  }

  /**
   * Alert the user that they have reached the maximum quantity of an item that can be selected.
   */
  static alertMaximumQuantityReached() {
    CartControls.defaultAlertHandler(CartControls.ALERTS.maximumQuantityReached);
  }

  /**
   * Alert the user that they have reached the maximum quantity of an item that can be selected.
   */
  alertMaximumQuantityReached() {
    this.alertHandler(CartControls.ALERTS.maximumQuantityReached);
  }

  /**
   * Alert the user that the item failed to be added to the cart for an unknown reason,
   * always via the legacy WCSimpleToast.
   *
   * This should never happen in production, but can be possible in development.
   * @see alertFailedToAddToCart the instance method, which respects a configured `alertHandler`.
   */
  static alertFailedToAddToCart() {
    CartControls.defaultAlertHandler(CartControls.ALERTS.failedToAddToCart);
  }

  /**
   * Alert the user that the item failed to be added to the cart for an unknown reason.
   *
   * This should never happen in production, but can be possible in development.
   */
  alertFailedToAddToCart() {
    this.alertHandler(CartControls.ALERTS.failedToAddToCart);
  }

  /**
   * Alert the user that they cannot add a gift card item to their cart.
   */
  alertGiftCardItem() {
    this.alertHandler({
      variant: 'danger',
      title: 'Cannot Add Item to Cart',
      message: this.$t('giftCardCartAlert'),
      timeout: 7000,
    });
  }

  /**
   * Alert the user that they are not shopping from their home store.
   */
  static async alertNotShoppingFromHomeStore() {
    try {
      await ModalService.open(WCStoreSelectModal);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Set a new quantity for an item in the cart as long as it is valid.
   */
  async setCustomQuantity(
    quantity: number | string | BigNumber,
    weightedQuantity: number | string | BigNumber | null,
    item: ItemSummary,
    variation: Variation,
    priceEmbeddedLineItem?: PriceEmbeddedLineItem | null,
    showStoreSelectModal: boolean = true,
    entryMethod: EntryMethod = ENTRY_METHOD.DEFAULT,
  ) {
    let newQuantity = new BigNumber(quantity);

    let newWeightedQuantity = new BigNumber(weightedQuantity);
    if (newQuantity.isZero() || newWeightedQuantity.isZero()) {
      newQuantity = new BigNumber(0);
      newWeightedQuantity = new BigNumber(0);
    }
    try {
      await this.setQuantity(
        newQuantity,
        newWeightedQuantity,
        item,
        variation,
        priceEmbeddedLineItem,
        showStoreSelectModal,
        entryMethod,
      );
    } catch (error) {
      console.error(error);
      if (error instanceof InvalidQuantityError) {
        this.alertMaximumQuantityReached();
      } else {
        this.alertFailedToAddToCart();
      }
      throw error;
    }
  }

  /* Builds the list of weight choices. */
  getWeightedQuantityOptions(item: ItemSummary, variation: Variation) {
    if (!isSoldByRandomWeight(item)) {
      return [];
    }
    const minimum = CartControls.getMinimumQuantity(item);
    const step = CartControls.getQuantityIncrement(item);
    const selectMax = CartControls.getSelectMax(item);

    if (step.isLessThanOrEqualTo(0)) {
      return [];
    }
    const currentQuantity = this.recoverUnroundedQuantityFromCart(item, variation);
    const weightedQuantity = currentQuantity.isZero()
      ? CartControls.getMinimumWeightedQuantity(item)
      : this.getWeightedQuantityInCart(item, variation);
    const currentDisplay = CartControls.getDisplayQuantity(item, currentQuantity);
    const options = [];
    for (
      let weight = minimum;
      weight.isLessThanOrEqualTo(selectMax) &&
      this.isQuantityValidForCart(item, variation, weight, weightedQuantity);
      weight = weight.plus(step)
    ) {
      options.push({
        value: weight,
        label: CartControls.getDisplayQuantity(item, weight),
        selected: CartControls.getDisplayQuantity(item, weight) === currentDisplay,
      });
    }
    return options;
  }

  /**
   * get the stock badge.
   */
  getStockBadge(item: ItemSummary) {
    const inStock = CartControls.isInStock(item);
    const inStoreOnly = CartControls.isInStoreOnly(item);
    const isAvailable = CartControls.isAvailable(item);
    const isOutOfStock = isAvailable && !inStock && !inStoreOnly;
    return isOutOfStock ? { icon: 'ban', text: this.$t('outOfStock') } : null;
  }

  static isMadeToOrder(item: ItemSummary) {
    return Boolean(item.hasModifiers || item.hasLinkedItems);
  }
}
